import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useRouter } from 'expo-router';
import { useAtomValue } from 'jotai';
import { View } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { RiveAnimation } from '@/components/RiveAnimation';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { ScrollView } from '@/components/ui/scroll-view';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { type RiveAnimationName } from '@/constants/riveAnimations';
import { tabSelectedKeyAtom } from '@/services/globalState';

type EmptyTabProps = {
  title: string;
  description: string;
  animation: RiveAnimationName;
  tabKey: string;
  tabSelectedKey: string;
};

type EmptyStateConfig = {
  animation: RiveAnimationName;
  title: string;
  description: string;
};

const EMPTY_STATE_CONFIG: Record<
  string,
  EmptyStateConfig | ((selectedCancerFocus: string) => EmptyStateConfig)
> = {
  default: {
    animation: 'GAIN_CLARITY',
    title: 'Primum experts are here to help',
    description: 'Start an inquiry now, and receive an answer in less than 24 hours.',
  },
  open: {
    animation: 'GAIN_CLARITY',
    title: 'Primum experts are here to help',
    description: 'Start an inquiry now, and receive an answer in less than 24 hours.',
  },
  closed: {
    animation: 'GAIN_CLARITY',
    title: 'Nothing to see here!',
    description: "Once you close an inquiry, you'll be able to reference it here.",
  },
  drafts: {
    animation: 'POWER_IN_NUMBERS',
    title: "We've got you covered",
    description: "We'll keep all your unfinished thoughts safe right here.",
  },
  filtered: (selectedCancerFocus: string) => ({
    animation: 'GAIN_CLARITY',
    title: 'No inquiries found',
    description: `There are no inquiries for ${selectedCancerFocus}. Try selecting a different cancer type or removing the filter.`,
  }),
};

export function getEmptyTabProps(params: {
  tabKey: string;
  tabSelectedKey: string;
  selectedCancerFocus?: string;
}): EmptyTabProps {
  const { tabKey, tabSelectedKey, selectedCancerFocus } = params;

  if (selectedCancerFocus) {
    const filteredConfig = EMPTY_STATE_CONFIG.filtered as (
      selectedCancerFocus: string
    ) => EmptyStateConfig;

    return {
      tabKey,
      tabSelectedKey,
      ...filteredConfig(selectedCancerFocus),
    };
  }

  const config = (EMPTY_STATE_CONFIG[tabKey] || EMPTY_STATE_CONFIG.default) as EmptyStateConfig;

  return {
    tabKey,
    tabSelectedKey,
    ...config,
  };
}

export function EmptyTab({ title, description, animation, tabKey, tabSelectedKey }: EmptyTabProps) {
  const { navigate } = useRouter();

  const selectedTab = useAtomValue(tabSelectedKeyAtom);

  return (
    <ScrollView
      testID="inbox-empty"
      contentContainerClassName={twJoin(
        'flex-1 justify-center p-5',
        !isWeb ? 'bg-background-100' : 'bg-background-50'
      )}
      bounces={false}>
      <VStack className="mb-6 h-[150px] w-full">
        {tabKey === selectedTab ? (
          <View style={{ flex: 1, pointerEvents: 'none' }}>
            <RiveAnimation name={animation} autoplay />
          </View>
        ) : null}
      </VStack>

      <Heading size="h2-2xl" className="mb-2 text-center text-primary-500">
        {title}
      </Heading>

      <Text className="mb-6 text-center">{description}</Text>

      <Button onPress={() => navigate('/directory')} className="w-full web:w-fit web:self-center">
        <ButtonText>Find an expert</ButtonText>
      </Button>
    </ScrollView>
  );
}
